import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const PROMO_PROPERTY = '_discount_code_auto_add';
const UPDATE_SOURCE = 'discount-code-auto-add';

/**
 * @typedef {{
 *   enabled: boolean;
 *   code: string;
 *   variantId: number | string;
 *   quantity: number;
 * }} PromoSettings
 *
 * @typedef {{
 *   key: string;
 *   line: number;
 *   quantity: number;
 *   variant_id: number;
 *   properties?: Record<string, string>;
 *   line_level_discount_allocations?: Array<{
 *     discount_application?: { title?: string; type?: string };
 *   }>;
 * }} CartItem
 *
 * @typedef {{
 *   items: CartItem[];
 *   discount_codes?: Array<{ code: string; applicable: boolean }>;
 *   cart_level_discount_applications?: Array<{ title: string; type?: string }>;
 *   sections?: Record<string, string>;
 * }} Cart
 */

class DiscountCodeAutoAddPromotion {
  /** @type {PromoSettings} */
  settings;

  /** @type {boolean} */
  isSyncing = false;

  /** @param {PromoSettings} settings */
  constructor(settings) {
    this.settings = {
      ...settings,
      code: normalizeCode(settings.code),
      quantity: Math.max(Number(settings.quantity) || 1, 1),
      variantId: Number(settings.variantId),
    };
  }

  init() {
    document.addEventListener(ThemeEvents.discountUpdate, this.sync);
    document.addEventListener(ThemeEvents.cartUpdate, this.sync);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.sync, { once: true });
    } else {
      this.sync();
    }
  }

  /** @param {Event} [event] */
  sync = async (event) => {
    if (this.isSyncing) return;

    const source = /** @type {CustomEvent | undefined} */ (event)?.detail?.data?.source;
    if (source === UPDATE_SOURCE) return;

    this.isSyncing = true;

    try {
      const cart = await this.fetchCart();
      const promoLines = this.findPromoLines(cart);

      if (this.hasActiveDiscount(cart)) {
        await this.ensurePromoItem(cart, promoLines);
      } else if (promoLines.length > 0) {
        await this.removePromoItems(promoLines);
      }
    } catch (error) {
      console.error('Discount code auto-add promotion failed:', error);
    } finally {
      this.isSyncing = false;
    }
  };

  matchesCode(code) {
    return normalizeCode(code) === this.settings.code;
  }

  /** @returns {Promise<boolean>} */
  async ensurePromoItemForSubmittedCode() {
    const cart = await this.fetchCart();
    await this.ensurePromoItem(cart, this.findPromoLines(cart));
    return true;
  }

  /** @returns {Promise<void>} */
  async removePromoItemsForSubmittedCode() {
    const cart = await this.fetchCart();
    await this.removePromoItems(this.findPromoLines(cart));
  }

  /** @returns {Promise<Cart>} */
  async fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /** @param {Cart} cart */
  hasActiveDiscount(cart) {
    return Boolean(
      cart.discount_codes?.some((discount) => discount.applicable && this.matchesCode(discount.code)) ||
        cart.cart_level_discount_applications?.some((discount) => this.matchesCode(discount.title)) ||
        cart.items?.some((item) =>
          item.line_level_discount_allocations?.some((allocation) =>
            this.matchesCode(allocation.discount_application?.title)
          )
        )
    );
  }

  /**
   * @param {Cart} cart
   * @returns {CartItem[]}
   */
  findPromoLines(cart) {
    return (cart.items || []).filter((item, index) => {
      item.line = index + 1;

      return (
        Number(item.variant_id) === this.settings.variantId &&
        normalizeCode(item.properties?.[PROMO_PROPERTY]) === this.settings.code
      );
    });
  }

  /**
   * @param {Cart} cart
   * @param {CartItem[]} promoLines
   */
  async ensurePromoItem(cart, promoLines) {
    const matchingQuantity = promoLines.reduce((total, item) => total + item.quantity, 0);

    if (matchingQuantity === this.settings.quantity && promoLines.length === 1) return;

    if (promoLines.length > 0) {
      await this.removePromoItems(promoLines, false);
    }

    await this.addPromoItem();
  }

  async addPromoItem() {
    const body = {
      items: [
        {
          id: this.settings.variantId,
          quantity: this.settings.quantity,
          properties: {
            [PROMO_PROPERTY]: this.settings.code,
          },
        },
      ],
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    };

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (data.status) {
      console.error('Discount code auto-add promotion could not add item:', data.description || data.message);
      return;
    }

    this.dispatchCartUpdate(data);
  }

  /**
   * @param {CartItem[]} promoLines
   * @param {boolean} dispatchUpdate
   */
  async removePromoItems(promoLines, dispatchUpdate = true) {
    for (const item of [...promoLines].sort((a, b) => b.line - a.line)) {
      const data = await this.changeLine(item.line, 0);

      if (dispatchUpdate) {
        this.dispatchCartUpdate(data);
      }
    }
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @returns {Promise<Cart>}
   */
  async changeLine(line, quantity) {
    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname,
      }),
    });

    return response.json();
  }

  getSectionsToRender() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  /** @param {Cart} data */
  dispatchCartUpdate(data) {
    document.dispatchEvent(
      new CartUpdateEvent(data, UPDATE_SOURCE, {
        source: UPDATE_SOURCE,
        sections: data.sections,
        itemCount: data.items?.reduce((total, item) => total + item.quantity, 0),
      })
    );
  }
}

class DiscountCodeAutoAddPromotionManager {
  /** @type {DiscountCodeAutoAddPromotion[]} */
  promotions;

  /** @param {PromoSettings[]} settings */
  constructor(settings) {
    this.promotions = settings.map((promotion) => new DiscountCodeAutoAddPromotion(promotion));
  }

  init() {
    for (const promotion of this.promotions) {
      promotion.init();
    }
  }

  /** @param {string} code */
  async ensureItemForCode(code) {
    const promotion = this.findPromotion(code);
    if (!promotion) return false;

    return promotion.ensurePromoItemForSubmittedCode();
  }

  /** @param {string} code */
  async removeItemForCode(code) {
    const promotion = this.findPromotion(code);
    if (!promotion) return;

    await promotion.removePromoItemsForSubmittedCode();
  }

  /** @param {string} code */
  findPromotion(code) {
    return this.promotions.find((promotion) => promotion.matchesCode(code));
  }
}

/** @param {string | undefined | null} code */
function normalizeCode(code) {
  return String(code || '')
    .trim()
    .toLowerCase();
}

const promotionSettings = /** @type {{ promotions?: PromoSettings[] } | undefined} */ (
  Theme.discountCodeAutoAddPromotion
);

const enabledPromotions = (promotionSettings?.promotions || []).filter(
  (promotion) => promotion.enabled && normalizeCode(promotion.code) && Number(promotion.variantId)
);

if (enabledPromotions.length > 0) {
  const manager = new DiscountCodeAutoAddPromotionManager(enabledPromotions);
  manager.init();
  window.DiscountCodeAutoAddPromotions = manager;
}
